/**
 * PlanIncludedList
 *
 * "What's included in every plan": the one shared list under the plan rows (US-58, BR-113). Its
 * wording is served by GET /api/public/plan-copy, never written in this app. Collapsible, closed by
 * default on narrow screens so the rows stay above the fold.
 */

import { useState } from 'react'
import { usePlanCopyQuery } from '../../hooks/useBilling'
import { Skeleton } from '../ui/skeleton'
import { Disclosure } from './Disclosure'
import { LoadErrorAlert } from './LoadErrorAlert'

/** Below this width (Tailwind `sm`) the list starts closed. */
const NARROW_QUERY = '(max-width: 639px)'

function startsOpen(): boolean {
  return typeof window.matchMedia !== 'function' || !window.matchMedia(NARROW_QUERY).matches
}

export function PlanIncludedList() {
  const { data, isLoading, isError, refetch, isFetching } = usePlanCopyQuery()
  const [open, setOpen] = useState(startsOpen)

  return (
    <div data-testid="plan-included-list">
      <Disclosure
        id="plan-included"
        toggleTestId="plan-included-toggle"
        label="What's included in every plan"
        open={open}
        onToggle={() => setOpen((v) => !v)}
      >
        {isLoading ? (
          <div className="flex flex-col gap-2" data-testid="plan-included-loading">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-4/5" />
          </div>
        ) : isError || !data ? (
          <LoadErrorAlert
            message="We couldn't load what's included. Try again."
            onRetry={() => void refetch()}
            retrying={isFetching}
            testId="plan-included-error"
            retryTestId="plan-included-retry"
          />
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {data.included.map((point) => (
              <li key={point.name} className="text-sm">
                <span className="font-semibold text-foreground">{point.name}</span>
                {point.description ? (
                  <span className="block text-muted-foreground">{point.description}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Disclosure>
    </div>
  )
}
